import React, { useEffect, useState } from 'react'
import { Container, Element, Heading, Image, Section } from 'react-bulma-components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { CheckStatusData } from '../../../../backend/src/services/product-items/product-items.schema'
import { useBikeRecallStatus } from '../../queries'
import Layout from '../layout/layout'
import { BiCheckCircle } from 'react-icons/bi'
import { IoWarningOutline } from 'react-icons/io5'
import { PDFDocument, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'

// @ts-expect-error
import downloadIcon from '../../assets/images/pdf-download.png'
import { useTranslations } from '../../hooks/use-translations'
import { Translate } from '../../utils/translations'

export const RecallStatusPage: React.FC = () => {
  const { serialNumber } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const firstName = searchParams.get('firstName')
  const lastName = searchParams.get('lastName')

  useEffect(() => {
    if (!serialNumber || !lastName || !firstName) {
      navigate('/')
    }
  }, [])
  if (!serialNumber || !lastName || !firstName) return <></>

  return (
    <Layout>
      <Section>
        <Container>
          <RecallStatus serialNumber={serialNumber} firstName={firstName} lastName={lastName} />
        </Container>
      </Section>
    </Layout>
  )
}

export const RecallStatus: React.FC<{
  serialNumber: CheckStatusData['serialNumber']['complete']
  firstName: CheckStatusData['requestor']['firstName']
  lastName: CheckStatusData['requestor']['lastName']
}> = ({ serialNumber, firstName, lastName }) => {
  const { current } = useTranslations()
  const navigate = useNavigate()
  const recallStatusQuery = useBikeRecallStatus(serialNumber)
  if (recallStatusQuery.data?.total === 0) navigate(`/${current}`)
  const recallStatus =
    recallStatusQuery?.data?.data &&
    recallStatusQuery?.data?.data[0] &&
    recallStatusQuery?.data?.data[0].status
  if (recallStatusQuery.isError) return <>Error</>
  if (recallStatusQuery.isFetching) return <>loading...</>
  if (recallStatus === 'good') return <GoodRecallStatusInstructions serialNumber={serialNumber} />
  if (recallStatus === 'defective')
    return (
      <DefectiveRecallStatusInstructions
        serialNumber={serialNumber}
        firstName={firstName}
        lastName={lastName}
      />
    )
  return <></>
}

export const GoodRecallStatusInstructions: React.FC<{ serialNumber?: string }> = ({
  serialNumber,
}) => {
  return (
    <Element textAlign={'center'}>
      <Element style={{ fontSize: 60 }} textColor="success">
        <BiCheckCircle />
      </Element>
      <Heading size={3}>
        <Translate value="home.form.confirmations.good.title"></Translate>
      </Heading>
      {serialNumber && (
        <Heading subtitle size={5}>
          S.N. : {serialNumber}
        </Heading>
      )}
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.good.p1"></Translate>
      </Element>
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.good.p2"></Translate>
      </Element>
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.good.p3"></Translate>
      </Element>
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.good.p4"></Translate>
      </Element>
    </Element>
  )
}

export const DefectiveRecallStatusInstructions: React.FC<{
  serialNumber: CheckStatusData['serialNumber']['complete']
  firstName: CheckStatusData['requestor']['firstName']
  lastName: CheckStatusData['requestor']['lastName']
}> = ({ serialNumber, firstName, lastName }) => {
  const { current } = useTranslations()
  const [pdfUrl, setPdfUrl] = useState<string>()

  useEffect(() => {
    const fetchPdf = async () => {
      fetch(`/work-orders/recall-${current}.pdf`)
        .then(res => res.arrayBuffer())
        .then(arrayBuffer => PDFDocument.load(arrayBuffer))
        .then(pdf => {
          pdf.registerFontkit(fontkit)
          return Promise.all([
            pdf,
            fetch('/fonts/NotoSans-Regular.ttf').then(res => res.arrayBuffer()),
          ])
        })
        .then(([pdf, fontBytes]) => {
          return Promise.all([pdf, pdf.embedFont(fontBytes)])
        })
        .then(([pdf, pdfFont]) => {
          // Get the first page of the document
          const pages = pdf.getPages()
          const firstPage = pages[0]

          // Get the width and height of the first page
          const { height } = firstPage.getSize()

          // Draw a string of text diagonally across the first page
          firstPage.drawText(`${firstName} ${lastName}\nS.N.: ${serialNumber}`, {
            x: 90,
            y: height - 60,
            size: 10,
            color: rgb(0, 0, 0),
            lineHeight: 12,
            font: pdfFont,
          })

          // Serialize the PDFDocument to bytes (a Uint8Array)
          return pdf.save()
        })
        .then(pdf => {
          const blob = new Blob([pdf], { type: 'application/pdf' })
          const blobUrl = URL.createObjectURL(blob)
          setPdfUrl(blobUrl)
        })
    }
    fetchPdf()
  }, [current])

  return (
    <Element textAlign={'center'}>
      <Element style={{ fontSize: 60 }} textColor="danger">
        <IoWarningOutline />
      </Element>
      <Heading size={3}>
        <Translate value="home.form.confirmations.defective.title"></Translate>
      </Heading>
      {serialNumber && (
        <Heading subtitle size={5}>
          S.N. : {serialNumber}
        </Heading>
      )}
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.defective.p1"></Translate>
      </Element>
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.defective.p2"></Translate>
      </Element>
      {/* We don't output decathlon locator instructions anymore */}
      {/* <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.defective.p3"></Translate> &nbsp;
        <Element
          renderAs="a"
          href={translate('home.form.confirmations.defective.decathlonLocatorLink')}
          textAlign="center"
        >
          <Translate value="home.form.confirmations.defective.decathlonLocatorLink"></Translate>
        </Element>
      </Element> */}
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.defective.p4"></Translate>
      </Element>
      <Element renderAs="p" mb={3}>
        <Element download renderAs="a" href={pdfUrl} display="inline-block" target={'_blank'}>
          <Image src={downloadIcon} style={{ maxWidth: 200 }} mt={5} mb={5} />
        </Element>
      </Element>
      <Element renderAs="p" mb={3}>
        <Translate value="home.form.confirmations.defective.p5"></Translate>
      </Element>
    </Element>
  )
}
